import { FC, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Col,
	Descriptions,
	Row,
	Spin,
	Statistic,
	Table,
	Tag,
	Tooltip,
	Typography,
	message
} from 'antd'
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ReloadOutlined,
	SyncOutlined,
	WarningOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { DashboardStats, fetchDashboard } from '../api/updates'
import {
	RNUpdatesMachines,
	RNUpdatesVersions
} from '../helpers/routeNames'

const { Title, Text } = Typography

const UpdatesDashboard: FC = () => {
	const [stats, setStats] = useState<DashboardStats | null>(null)
	const [loading, setLoading] = useState(false)

	const load = async () => {
		setLoading(true)
		try {
			const data = await fetchDashboard()
			setStats(data)
		} catch {
			message.error('Failed to load dashboard')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		load()
	}, [])

	if (loading || !stats) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					paddingTop: 80
				}}>
				<Spin size="large" />
			</div>
		)
	}

	const locationRows = Object.entries(stats.byLocation).map(
		([city, count]) => ({ city, count })
	)
	const carrierTypeRows = Object.entries(stats.byCarrierType).map(
		([slug, count]) => ({ slug, count })
	)

	return (
		<div className="carriers">
			<header className="page-header">
				<Title level={4} style={{ margin: 0 }}>
					Update System — Dashboard
				</Title>
				<Button
					icon={<ReloadOutlined />}
					onClick={load}
					loading={loading}>
					Refresh
				</Button>
			</header>

			<main>
				{/* Top stats */}
				<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
					<Col xs={24} sm={12} md={6}>
						<Card>
							<Statistic
								title="Total Machines"
								value={stats.totalMachines}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Card>
							<Statistic
								title="Success"
								value={stats.success}
								valueStyle={{ color: '#52c41a' }}
								prefix={<CheckCircleOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Card>
							<Statistic
								title="Pending"
								value={stats.pending}
								valueStyle={{ color: '#faad14' }}
								prefix={<SyncOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Card>
							<Statistic
								title="Failed"
								value={stats.failed}
								valueStyle={{ color: '#ff4d4f' }}
								prefix={<CloseCircleOutlined />}
							/>
						</Card>
					</Col>
				</Row>

				<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
					{/* Latest versions */}
					<Col xs={24} md={12}>
						<Card
							title="Latest Versions"
							extra={
								<Link to={RNUpdatesVersions}>
									View all
								</Link>
							}>
							{Object.keys(stats.latestVersions)
								.length === 0 ? (
								<Text type="secondary">
									No versions deployed yet
								</Text>
							) : (
								<Descriptions column={1} size="small">
									{Object.entries(
										stats.latestVersions
									).map(([slug, ver]) => (
										<Descriptions.Item
											key={slug}
											label={<Tag>{slug}</Tag>}>
											<strong>{ver}</strong>
										</Descriptions.Item>
									))}
								</Descriptions>
							)}
						</Card>
					</Col>

					{/* By carrier type */}
					<Col xs={24} md={12}>
						<Card title="Machines by Carrier Type">
							<Table
								dataSource={carrierTypeRows}
								rowKey="slug"
								size="small"
								pagination={false}
								columns={[
									{
										title: 'Carrier Type',
										dataIndex: 'slug',
										key: 'slug',
										render: (slug: string) => (
											<Link
												to={`${RNUpdatesMachines}?carrierType=${slug}`}>
												<Tag>{slug}</Tag>
											</Link>
										)
									},
									{
										title: 'Machines',
										dataIndex: 'count',
										key: 'count',
										align: 'right' as const
									}
								]}
							/>
						</Card>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					{/* By location */}
					<Col xs={24} md={12}>
						<Card title="Machines by Location">
							<Table
								dataSource={locationRows}
								rowKey="city"
								size="small"
								pagination={false}
								columns={[
									{
										title: 'City',
										dataIndex: 'city',
										key: 'city'
									},
									{
										title: 'Machines',
										dataIndex: 'count',
										key: 'count',
										align: 'right' as const
									}
								]}
							/>
						</Card>
					</Col>

					{/* Recent errors */}
					<Col xs={24} md={12}>
						<Card
							title={
								<span>
									<WarningOutlined
										style={{
											color: '#ff4d4f',
											marginRight: 8
										}}
									/>
									Recent Errors
								</span>
							}
							extra={
								<Link
									to={`${RNUpdatesMachines}?status=failed`}>
									View all failed
								</Link>
							}>
							{stats.recentErrors.length === 0 ? (
								<Text type="secondary">
									No recent errors
								</Text>
							) : (
								<Table
									dataSource={stats.recentErrors.map(
										(e, i) => ({
											...e,
											key: i
										})
									)}
									size="small"
									pagination={false}
									columns={[
										{
											title: 'Machine',
											dataIndex: 'machineId',
											key: 'machineId',
											render: (id: string) => (
												<Tooltip title={id}>
													<Text
														code
														style={{
															fontSize: 12,
															maxWidth: 120,
															display:
																'inline-block',
															overflow:
																'hidden',
															textOverflow:
																'ellipsis',
															whiteSpace:
																'nowrap'
														}}>
														{id}
													</Text>
												</Tooltip>
											)
										},
										{
											title: 'Ver',
											dataIndex: 'version',
											key: 'version',
											width: 80
										},
										{
											title: 'Error',
											dataIndex: 'error',
											key: 'error',
											render: (err: string) => (
												<Tooltip title={err}>
													<Text
														type="danger"
														style={{
															maxWidth: 180,
															display:
																'inline-block',
															overflow:
																'hidden',
															textOverflow:
																'ellipsis',
															whiteSpace:
																'nowrap'
														}}>
														{err}
													</Text>
												</Tooltip>
											)
										}
									]}
								/>
							)}
						</Card>
					</Col>
				</Row>
			</main>
		</div>
	)
}

export default UpdatesDashboard
